import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Card, { CardBody, CardHeader } from '../common/Card';
import Button from '../common/Button';
import GradientText from '../common/GradientText';
import { supabase } from '../../config/supabase';
import { useAuthStore } from '../../store/authStore';
import { format, subDays, isWithinInterval, startOfDay } from 'date-fns';
import ReadingChart from './ReadingChart';
import SubjectChart from './SubjectChart';

interface Reading {
  id: string;
  created_at: string;
  subject: string;
  reading_title: string;
  context: string;
  spread_type: string;
  cards: any[];
  user_interpretation: string;
  ai_interpretation: string;
  themes: any[];
  advice: string;
  question: string;
}

type Section = 'overview' | 'readings';

const ADMIN_EMAIL = 'taramccann@gmail.com';

const AdminPanel: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [activeSection, setActiveSection] = useState<Section>('overview');
  const [readings, setReadings] = useState<Reading[]>([]);
  const [filteredReadings, setFilteredReadings] = useState<Reading[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchReadings = async () => {
      try {
        // Verify if the current user is admin
        if (!user?.email || user.email !== ADMIN_EMAIL) {
          navigate('/');
          return;
        }

        // Fetch all readings
        const { data: readingsData, error: readingsError } = await supabase
          .from('readings')
          .select('*')
          .order('created_at', { ascending: false });

        if (readingsError) throw readingsError;

        setReadings(readingsData || []);
        setFilteredReadings(readingsData || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching readings:', err);
        setError(err instanceof Error ? err.message : 'An error occurred while fetching data');
      } finally {
        setLoading(false);
      }
    };

    fetchReadings();
  }, [user, navigate]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredReadings(readings);
      return;
    }

    const searchTermLower = searchTerm.toLowerCase();
    const filtered = readings.filter(reading => {
      const title = reading.reading_title?.toLowerCase() || '';
      const content = reading.ai_interpretation?.toLowerCase() || '';
      const context = reading.context?.toLowerCase() || '';
      const userInterpretation = reading.user_interpretation?.toLowerCase() || '';

      return (
        title.includes(searchTermLower) ||
        content.includes(searchTermLower) ||
        context.includes(searchTermLower) ||
        userInterpretation.includes(searchTermLower)
      );
    });

    setFilteredReadings(filtered);
  }, [searchTerm, readings]);

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const formatSpreadType = (type: string) => {
    return type
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getReadingStats = () => {
    const now = new Date();
    const today = startOfDay(now);
    const yesterday = subDays(today, 1);
    const lastWeek = subDays(today, 7);
    const lastMonth = subDays(today, 30);

    return {
      total: readings.length,
      today: readings.filter(r => 
        isWithinInterval(new Date(r.created_at), { start: today, end: now })
      ).length,
      yesterday: readings.filter(r => 
        isWithinInterval(new Date(r.created_at), { start: yesterday, end: today })
      ).length,
      lastWeek: readings.filter(r => 
        isWithinInterval(new Date(r.created_at), { start: lastWeek, end: now })
      ).length,
      lastMonth: readings.filter(r => 
        isWithinInterval(new Date(r.created_at), { start: lastMonth, end: now })
      ).length
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sun"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        <Card>
          <CardBody>
            <div className="text-red-600 dark:text-red-400 p-4">
              Error: {error}
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  const stats = getReadingStats();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="max-w-6xl mx-auto"
    >
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-display font-bold">
                <GradientText>Admin Dashboard</GradientText>
              </h1>
              <p className="text-ink/70 dark:text-parchment/60 mt-2">
                Monitor reading activity and trends
              </p>
            </div>
          </div>
        </CardHeader>

        <CardBody className="p-6">
          <div className="flex flex-wrap gap-4 mb-6">
            <Button
              variant={activeSection === 'overview' ? 'primary' : 'outline'}
              onClick={() => setActiveSection('overview')}
            >
              Overview
            </Button>
            <Button
              variant={activeSection === 'readings' ? 'primary' : 'outline'}
              onClick={() => setActiveSection('readings')}
            >
              Readings
            </Button>
          </div>

          {activeSection === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <CardBody className="p-4">
                    <h3 className="text-sm font-medium text-ink/50 dark:text-parchment/60">Total Readings</h3>
                    <p className="text-2xl font-bold text-sun dark:text-ember">{stats.total}</p>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody className="p-4">
                    <h3 className="text-sm font-medium text-ink/50 dark:text-parchment/60">Readings Today</h3>
                    <p className="text-2xl font-bold text-sun dark:text-ember">{stats.today}</p>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody className="p-4">
                    <h3 className="text-sm font-medium text-ink/50 dark:text-parchment/60">Yesterday</h3>
                    <p className="text-2xl font-bold text-sun dark:text-ember">{stats.yesterday}</p>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody className="p-4">
                    <h3 className="text-sm font-medium text-ink/50 dark:text-parchment/60">Last 7 Days</h3>
                    <p className="text-2xl font-bold text-sun dark:text-ember">{stats.lastWeek}</p>
                  </CardBody>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <h2 className="text-xl font-medium text-ink dark:text-parchment">Reading Activity</h2>
                </CardHeader>
                <CardBody>
                  <ReadingChart readings={readings} />
                </CardBody>
              </Card>

              <Card>
                <CardHeader>
                  <h2 className="text-xl font-medium text-ink dark:text-parchment">Reading Subjects</h2>
                </CardHeader>
                <CardBody>
                  <SubjectChart readings={readings} />
                </CardBody>
              </Card>

              <Card>
                <CardHeader>
                  <h2 className="text-xl font-medium text-ink dark:text-parchment">Recent Activity</h2>
                </CardHeader>
                <CardBody>
                  <div className="space-y-4">
                    {readings.slice(0, 5).map((reading) => (
                      <div key={reading.id} className="flex items-center justify-between py-2">
                        <div>
                          <p className="font-medium text-ink dark:text-parchment">{reading.reading_title}</p>
                          <p className="text-sm text-ink/50 dark:text-parchment/60">
                            {reading.subject}
                          </p>
                        </div>
                        <span className="text-sm text-ink/50 dark:text-parchment/60">
                          {format(new Date(reading.created_at), 'PPp')}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
            </div>
          )}

          {activeSection === 'readings' && (
            <div className="space-y-6">
              <div className="flex justify-end">
                <div className="relative w-64">
                  <input
                    type="text"
                    placeholder="Search readings..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-mist dark:border-dusk rounded-md focus:outline-none focus:ring-2 focus:ring-moon bg-vellum dark:bg-shadow"
                  />
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-mist text-sm">✧</span>
                </div>
              </div>

              <div className="space-y-4">
                {filteredReadings.map((reading) => (
                  <div
                    key={reading.id}
                    className="border border-mist dark:border-dusk rounded-lg overflow-hidden bg-vellum dark:bg-shadow"
                  >
                    <div
                      className="p-4 cursor-pointer hover:bg-parchment dark:hover:bg-shadow transition-colors"
                      onClick={() => toggleExpand(reading.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-medium text-ink dark:text-parchment">
                            {reading.reading_title || 'Untitled Reading'}
                          </h3>
                          <div className="flex flex-wrap items-center gap-3 text-sm text-ink/70 dark:text-parchment/60">
                            <div className="flex items-center gap-1">
                              <span>{format(new Date(reading.created_at), 'PPP')}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="px-2 py-0.5 bg-vellum dark:bg-shadow/30 text-sun dark:text-ember rounded-full">
                                {formatSpreadType(reading.spread_type)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <span className="text-mist text-lg">{expandedId === reading.id ? '−' : '+'}</span>
                      </div>
                    </div>

                    {expandedId === reading.id && (
                      <div className="border-t border-mist dark:border-dusk p-4">
                        <div className="space-y-6">
                          <div>
                            <h4 className="font-medium text-ink dark:text-parchment mb-2">Question & Context</h4>
                            <div className="space-y-2">
                              <p className="text-ink dark:text-parchment/60 font-medium">{reading.question}</p>
                              <p className="text-ink/70 dark:text-parchment/60">{reading.context}</p>
                            </div>
                          </div>

                          <div>
                            <h4 className="font-medium text-ink dark:text-parchment mb-2">Cards</h4>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                              {reading.cards.map((card, index) => (
                                <div 
                                  key={index}
                                  className="bg-parchment dark:bg-shadow p-3 rounded-md"
                                >
                                  <span className="text-sun dark:text-ember text-xs block mb-1">
                                    {card.position}
                                  </span>
                                  <span className="text-ink dark:text-parchment font-medium">
                                    {card.name}
                                  </span>
                                  {card.isReversed && (
                                    <span className="text-sun dark:text-ember text-xs block mt-1">
                                      Reversed
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>

                          {reading.themes && reading.themes.length > 0 && (
                            <div>
                              <h4 className="font-medium text-ink dark:text-parchment mb-2">Themes</h4>
                              <div className="flex flex-wrap gap-2">
                                {reading.themes.map((theme, index) => (
                                  <span
                                    key={index}
                                    className={`px-2.5 py-0.5 rounded-full text-sm ${theme.color}`}
                                  >
                                    {theme.name}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          <div>
                            <h4 className="font-medium text-ink dark:text-parchment mb-2">AI Interpretation</h4>
                            <div className="bg-parchment dark:bg-shadow/20 p-4 rounded-md border border-vellum dark:border-dusk">
                              {reading.ai_interpretation.split('\n\n').map((paragraph, index) => (
                                <p key={index} className="text-shadow dark:text-parchment/20 mb-4 last:mb-0">
                                  {paragraph}
                                </p>
                              ))}
                            </div>
                          </div>

                          <div>
                            <h4 className="font-medium text-ink dark:text-parchment mb-2">Advice</h4>
                            <div className="bg-parchment dark:bg-shadow/20 p-4 rounded-md border border-vellum dark:border-dusk">
                              <p className="text-shadow dark:text-parchment/20">{reading.advice}</p>
                            </div>
                          </div>

                          {reading.user_interpretation && (
                            <div>
                              <h4 className="font-medium text-ink dark:text-parchment mb-2">User's Interpretation</h4>
                              <div className="bg-parchment dark:bg-shadow p-4 rounded-md border border-mist dark:border-dusk">
                                <p className="text-ink dark:text-parchment/60">{reading.user_interpretation}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {filteredReadings.length === 0 && (
                  <div className="text-center py-8 text-ink/70 dark:text-parchment/60">
                    No readings found matching your search.
                  </div>
                )}
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    </motion.div>
  );
};

export default AdminPanel;